/**
 * Los artículos — la segunda cola de trabajo del paso 3.
 *
 * Dos decisiones que no son de acomodo:
 *
 * 1. **Leerlo es verlo como va a quedar.** Aprobar sin leer es aprobar algo
 *    que no viste (REGLA #21): «Ver cómo se ve» abre la página del artículo en
 *    otra pestaña, con el estilo del sitio, la portada y el cuerpo entero. La
 *    tarjeta es solo título, portada y acciones (Eduardo, 2026-10-18: el
 *    resumen, el tema de origen y el cajón de lectura eran ruido que no leía;
 *    «leer completo» y «ver cómo se ve» eran lo mismo).
 *
 * 2. **Publicar ahora pregunta; aprobar y agendar no.** Publicar escribe en el
 *    servidor del cliente y queda público — es de lo que no vuelve. Aprobar con
 *    fecha no publica nada hoy: lo hace el worker cuando llegue el día. El
 *    criterio es hacia dónde sale el error, no cuán grande es el cambio
 *    (REGLA #24).
 *
 * Layout (handoff paso3-1b): cabecera con el resumen del ritmo y los ajustes
 * plegados; una banda con cuántos esperan y a dónde se publican, UNA vez; una
 * tarjeta por artículo que espera revisión con la acción en una columna fija;
 * y un libro mayor de dos columnas para lo agendado y lo publicado.
 *
 * Desde M9 el ritmo es "artículos por semana": la revisión semanal los escribe
 * TODOS el día que corre, cada uno con su fecha repartida en la semana, y
 * Eduardo los aprueba con esa fecha (o la cambia). La semana se ve en una tira
 * de siete días con lo que cae en cada uno.
 */
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../../ui/DetailSection.js';
import { SectionHeaderAction } from '../../ui/SectionHeaderAction.js';
import { Button } from '../../ui/Button.js';
import { Input } from '../../ui/Input.js';
import { Textarea } from '../../ui/Textarea.js';
import { Pill } from '../../PillGroup.js';
import { Dropdown } from '../../ui/Dropdown.js';
import { FileUploadZone } from '../../ui/FileUploadZone.js';
import { useConfirm } from '../../ui/ConfirmDialog.js';
import { useToast } from '../../ui/Toast.js';
import { KVGrid } from '../../ui/KVGrid.js';
import { KVRow } from '../../ui/KVRow.js';
import type { Articulo } from '../../../hooks/useMantenimiento.js';
import { useArticulos, useAccionArticulo, useMantenimiento, useFijarArticulosPorSemana, useFijarEstiloImagenes, type EnvioDeRevision } from '../../../hooks/useMantenimiento.js';
import { useSiteConnection } from '../../../hooks/useSiteConnection.js';
import { fechaLarga } from './SeccionEstaSemana.js';
import { useAnchoDeContenedor, pxDeToken } from '../../../hooks/useMediaQuery.js';

const menor = {
  fontSize: 'var(--evo-fs-tiny)',
  color: 'var(--evo-fg-4)',
  fontFamily: 'var(--evo-font)',
} as const;

const etiqueta = {
  ...menor,
  textTransform: 'uppercase',
  letterSpacing: 'var(--evo-ls-caps)',
} as const;

const REJILLA = {
  display: 'grid',
  gridTemplateColumns: 'var(--evo-articulo-portada-w) minmax(0, 1fr) var(--evo-articulo-accion-w)',
  gap: 'var(--evo-space-5)',
  alignItems: 'start',
} as const;

/**
 * La fecha de hoy donde está Eduardo, no en UTC. A las 9 de la noche en
 * Florida `toISOString()` ya dice mañana, y "le toca hoy" se corría un día.
 */
function hoyLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Abre el artículo como página del sitio, en otra pestaña. */
function verComoQueda(projectId: number, articuloId: number): void {
  window.open(`/api/projects/${String(projectId)}/articulos/${String(articuloId)}/vista`, '_blank', 'noopener');
}

export function SeccionArticulos({ projectId }: { projectId: number }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { data: estado } = useMantenimiento(projectId);
  const { data } = useArticulos(projectId, estado?.enabled === true);
  const { data: conexion } = useSiteConnection(projectId);
  const fijarPorSemana = useFijarArticulosPorSemana(projectId);
  const fijarEstilo = useFijarEstiloImagenes(projectId);
  const [verAjustes, setVerAjustes] = useState(false);
  const [verQuePasa, setVerQuePasa] = useState(false);

  // Sin mantenimiento no hay cola de artículos: el riel muestra la opción de
  // encenderlo, y acá no se dibuja nada.
  if (!estado?.enabled) return null;

  const articulos = data?.articulos ?? [];
  const esperando = articulos.filter(a => a.status === 'borrador' || a.status === 'cambios');
  const agendados = articulos.filter(a => a.status === 'aprobado');
  const publicados = articulos.filter(a => a.status === 'publicado');

  const porSemana = estado.articulosPorSemana;
  const proxima = estado.revision.proxima;
  const fmtDia = (f: string) => new Date(`${f}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const base = conexion?.connection?.siteUrl ? `${conexion.connection.siteUrl.replace(/\/$/, '')}/blog/` : null;

  // El resumen del ritmo en la cabecera; la frase larga, plegada con los ajustes.
  const resumenDelRitmo = porSemana === 0
    ? t('projects.maintenance.articles.perWeekOff')
    : [t('projects.maintenance.articles.perWeek', { count: porSemana }), proxima ? t('projects.maintenance.articles.nextReview', { fecha: fmtDia(proxima.fecha) }) : null]
      .filter(Boolean).join(' · ');
  const fraseDelRitmo = porSemana === 0
    ? t('projects.maintenance.articles.rhythmOff')
    : estado.autonomia === 'manual'
      ? t('projects.maintenance.articles.rhythmManual')
      : t('projects.maintenance.articles.rhythmOn', { count: porSemana, fecha: proxima ? fechaLarga(proxima.fecha, i18n.language) : '—' });

  // Abierta solo si hay artículos esperando revisión, que es lo que hay que
  // hacer acá; agendados y publicados son registro y se cuentan en la
  // cabecera. Los artículos llegan en otra consulta: la tarjeta se monta de
  // nuevo al llegar para decidir con el dato y no con el vacío de la carga.
  return (
    <DetailSection
      key={data ? 'articulos' : 'articulos-cargando'}
      title={t('projects.maintenance.articles.title')}
      count={articulos.length}
      padding="none"
      collapsible
      defaultOpen={esperando.length > 0}
      storageKey={`articulos:${String(projectId)}`}
      titleRight={
        <>
          <span style={{ ...menor, whiteSpace: 'nowrap' }}>{resumenDelRitmo}</span>
          <SectionHeaderAction tone="muted" onClick={() => { setVerAjustes(v => !v); }}>
            {t('projects.maintenance.articles.rhythmAndCovers')} {verAjustes ? '▴' : '▾'}
          </SectionHeaderAction>
        </>
      }
    >
      {verAjustes && (
        <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
          <KVGrid columns={1}>
            <KVRow
              label={t('projects.maintenance.articles.perWeekLabel')}
              value={
                <Dropdown
                  size="sm"
                  options={estado.opcionesPorSemana.map(n => ({ value: String(n), label: n === 0 ? t('projects.maintenance.articles.perWeekOff') : t('projects.maintenance.articles.perWeek', { count: n }) }))}
                  value={String(porSemana)}
                  onChange={v => {
                    fijarPorSemana.mutate(Number(v), {
                      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
                    });
                  }}
                  disabled={fijarPorSemana.isPending}
                />
              }
            />
            {/* El estilo de las portadas, en una frase, como el nicho. Se genera
                una imagen por artículo con el proveedor de Ajustes → IA. */}
            <KVRow
              label={t('projects.maintenance.articles.coverStyleLabel')}
              value={estado.estiloImagenes ?? ''}
              placeholder={t('projects.maintenance.articles.coverStylePlaceholder')}
              onSave={async v => { await fijarEstilo.mutateAsync(v); }}
              last
            />
          </KVGrid>
          <p style={{ ...menor, margin: 'var(--evo-space-2) 0 0', maxWidth: '70ch', lineHeight: 1.5 }}>{fraseDelRitmo}</p>
        </div>
      )}

      {/* La banda: cuántos esperan, que no se escribe otro, y a dónde se
          publican — UNA vez, no debajo de cada artículo. */}
      <div style={{
        padding: 'var(--evo-space-3) var(--evo-space-4)',
        background: 'var(--evo-panel)',
        borderBottom: '1px solid var(--evo-stroke)',
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--evo-space-2)',
      }}>
        <div style={{ flex: 1, minWidth: '12em' }}>
          <div style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
            {esperando.length > 0
              ? t('projects.maintenance.articles.waitingCount', { count: esperando.length })
              : t('projects.maintenance.articles.nothingWaiting')}
          </div>
          <div style={menor}>
            {[
              esperando.length > 0 ? t('projects.maintenance.articles.noNextUntil') : null,
              base ? t('projects.maintenance.articles.publishedAtBase', { base: base.replace(/^https?:\/\//, '') }) : null,
            ].filter(Boolean).join(' · ')}
          </div>
        </div>
        <Button size="xs" variant="link" tone="muted" onClick={() => { setVerQuePasa(v => !v); }}>
          {t('projects.maintenance.articles.whatHappens')} {verQuePasa ? '▴' : '▾'}
        </Button>
      </div>
      {verQuePasa && (
        <p style={{ ...menor, margin: 0, padding: 'var(--evo-space-3) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)', color: 'var(--evo-fg-3)', maxWidth: '80ch', lineHeight: 1.5 }}>
          {t('projects.maintenance.articles.destination', { url: base?.replace(/^https?:\/\//, '') ?? '/blog/' })}
        </p>
      )}

      {/* La semana: siete días desde la revisión, con lo que cae en cada uno.
          Es la respuesta a "¿qué sale y cuándo?" sin abrir cada tarjeta. */}
      {(esperando.length > 0 || agendados.length > 0) && (
        <TiraDeLaSemana articulos={[...esperando, ...agendados]} desde={estado.revision.ultima?.empezoEn ?? null} />
      )}

      <div style={{ padding: 'var(--evo-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        {articulos.length === 0
          ? (
            <p style={{ margin: 0, fontSize: 'var(--evo-fs-body)', color: 'var(--evo-text-muted)', fontFamily: 'var(--evo-font)' }}>
              {t('projects.maintenance.articles.empty')}
            </p>
          )
          : (
            <>
              {esperando.map(a => <TarjetaEnRevision key={a.id} articulo={a} projectId={projectId} />)}
              {(agendados.length > 0 || publicados.length > 0) && (
                <LibroMayor projectId={projectId} agendados={agendados} publicados={publicados} base={base} autonomia={estado.autonomia} />
              )}
            </>
          )}
      </div>
    </DetailSection>
  );
}

/** Un artículo que espera revisión: portada · texto · acción. */
function TarjetaEnRevision({ articulo, projectId }: { articulo: Articulo; projectId: number }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const accion = useAccionArticulo(projectId);
  const { confirm, dialog } = useConfirm();
  const [modo, setModo] = useState<null | 'agendar' | 'cambios'>(null);
  const [fecha, setFecha] = useState('');
  const [nota, setNota] = useState('');
  // Con portada, la zona para arrastrar otra se abre desde «Más».
  const [subiendoPortada, setSubiendoPortada] = useState(false);
  // Angosta (la columna de trabajo con el panel de Evo Brain abierto): los
  // botones van debajo del texto, en fila. Se decide por el ancho de la
  // tarjeta, no de la ventana.
  const { ref: tarjeta, ancho } = useAnchoDeContenedor<HTMLDivElement>();
  const apilada = ancho !== null && ancho < pxDeToken('--evo-articulo-bp');

  function correr(
    a: Parameters<typeof accion.mutate>[0]['accion'],
    despues?: () => void,
    conRespuesta?: (r: Awaited<ReturnType<typeof accion.mutateAsync>>) => void,
  ): void {
    accion.mutate({ articuloId: articulo.id, accion: a }, {
      onSuccess: r => { conRespuesta?.(r); despues?.(); },
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }

  /**
   * Agendar y mandárselo al cliente de una vez.
   *
   * Es el flujo que Eduardo describió y que no existía: la fecha queda puesta,
   * el cliente recibe el enlace, y cuando apruebe sale ese día sin volver acá.
   * En serie: si el correo falla, la fecha YA quedó guardada y se dice, porque
   * si no parece que no se hizo nada.
   */
  async function agendarYEnviar(): Promise<void> {
    try {
      await accion.mutateAsync({ articuloId: articulo.id, accion: { tipo: 'fecha', fecha } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('common.error'));
      return;
    }
    try {
      const r = await accion.mutateAsync({ articuloId: articulo.id, accion: { tipo: 'compartir' } });
      const envio = (r as Partial<{ envio: EnvioDeRevision }>).envio;
      setModo(null);
      if (envio?.ok) toast.success(t('projects.maintenance.articles.scheduledAndSent', { fecha: fmtDia(fecha), correo: envio.a ?? '' }));
      else toast.error(t('projects.maintenance.articles.sendFailed', { motivo: envio?.motivo ?? t('common.error') }));
    } catch (e) {
      setModo(null);
      toast.error(t('projects.maintenance.articles.sendFailed', { motivo: e instanceof Error ? e.message : t('common.error') }));
    }
  }

  async function publicarYa(): Promise<void> {
    const ok = await confirm({
      title: t('projects.maintenance.articles.publishNowTitle'),
      description: t('projects.maintenance.articles.publishNowBody'),
      confirmLabel: t('projects.maintenance.articles.publishNow'),
      tone: 'warning',
    });
    if (ok) correr({ tipo: 'publicar' });
  }

  // Borrar es de lo que no vuelve: pregunta. El tema vuelve a la lista.
  async function borrar(): Promise<void> {
    const ok = await confirm({
      title: t('projects.maintenance.articles.deleteTitle'),
      description: t('projects.maintenance.articles.deleteBody'),
      confirmLabel: t('projects.maintenance.articles.delete'),
      tone: 'danger',
    });
    if (ok) correr({ tipo: 'borrar' }, () => { toast.success(t('projects.maintenance.articles.deleted')); });
  }

  // Lo que no es la acción esperada va detrás de «Más»: publicar ahora,
  // reescribir, el enlace del cliente, la portada.
  const mas = [
    { value: 'publicar', label: t('projects.maintenance.articles.publishNow') },
    ...(articulo.status === 'cambios' ? [{ value: 'reescribir', label: t('projects.maintenance.articles.rewrite') }] : []),
    articulo.urlDelCliente
      ? { value: 'revocar', label: t('projects.maintenance.articles.revoke') }
      : { value: 'compartir', label: t('projects.maintenance.articles.share') },
    // Va JUNTO a «Enviar al cliente» porque es ahí donde se busca: Eduardo lo
    // fue a buscar al menú y no estaba (2026-09-17). Abre el panel de fecha.
    ...(articulo.status === 'borrador' || articulo.status === 'cambios'
      ? [{ value: 'agendarYEnviar', label: t('projects.maintenance.articles.scheduleAndSend') }]
      : []),
    ...(articulo.urlDelCliente ? [{ value: 'copiar', label: t('projects.maintenance.articles.copyLink') }] : []),
    { value: 'portada', label: t(articulo.coverPath ? 'projects.maintenance.articles.coverRegenerate' : 'projects.maintenance.articles.coverGenerate') },
    ...(articulo.coverPath ? [{ value: 'subirPortada', label: t('projects.maintenance.articles.coverUpload') }] : []),
    { value: 'borrar', label: t('projects.maintenance.articles.delete') },
  ];
  function desdeMas(v: string): void {
    switch (v) {
      case 'publicar': void publicarYa(); break;
      case 'reescribir': correr({ tipo: 'reescribir' }); break;
      case 'agendarYEnviar': setModo('agendar'); break;
      case 'compartir':
        // Este botón manda un correo real al cliente: se dice a quién llegó, o
        // que no llegó y el enlace igual quedó creado.
        correr({ tipo: 'compartir' }, undefined, r => {
          // La respuesta es una unión de todas las acciones; solo esta trae
          // `envio`. El acceso va tipado en vez de narrowing, que con la unión
          // completa deja `{}`.
          const envio = (r as Partial<{ envio: EnvioDeRevision }>).envio;
          if (envio?.ok) toast.success(t('projects.maintenance.articles.sentTo', { correo: envio.a ?? '' }));
          else toast.error(t('projects.maintenance.articles.sendFailed', { motivo: envio?.motivo ?? t('common.error') }));
        });
        break;
      case 'revocar': correr({ tipo: 'revocar' }); break;
      case 'portada': correr({ tipo: 'portada' }); break;
      case 'subirPortada': setSubiendoPortada(true); break;
      case 'borrar': void borrar(); break;
      case 'copiar':
        if (articulo.urlDelCliente) {
          void navigator.clipboard.writeText(articulo.urlDelCliente).then(() => { toast.success(t('projects.maintenance.articles.linkCopied')); });
        }
        break;
    }
  }

  const portada = articulo.coverPath
    ? `/api/projects/${String(projectId)}/articulos/${String(articulo.id)}/portada?v=${encodeURIComponent(articulo.updatedAt)}`
    : null;
  // La fecha que la revisión propuso. Aprobar la conserva; «Aprobar y agendar»
  // la cambia. Sin fecha propuesta, el botón principal abre el calendario.
  const propuesta = articulo.scheduledFor ? articulo.scheduledFor.slice(0, 10) : null;
  const fmtDia = (f: string) => new Date(`${f}T12:00:00Z`).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' });

  // El calendario abre con la fecha propuesta puesta: casi siempre es la que va.
  function abrirAgenda(): void {
    setFecha(f => f || (propuesta ?? ''));
    setModo(m => (m === 'agendar' ? null : 'agendar'));
  }

  /**
   * Qué hacer con la fecha elegida: un menú que ejecuta al elegir, en vez de
   * cuatro botones en fila (Eduardo, 2026-10-18: "me causa mucho ruido y tiendo
   * a perderme"). Lo que necesita fecha aparece cuando hay una; aprobar sin
   * fecha, siempre.
   */
  const opcionesDeAgenda = [
    ...(fecha
      ? [
        { value: 'agendarYEnviar', label: t('projects.maintenance.articles.scheduleAndSend') },
        { value: 'agendar', label: t('projects.maintenance.articles.schedule') },
        // Agendar SIN aprobar: la fecha queda puesta y el artículo espera al
        // cliente; cuando él apruebe ya está en su día (Eduardo, 2026-09-17).
        { value: 'soloAgendar', label: t('projects.maintenance.articles.onlySchedule') },
        ...(fecha === hoyLocal() ? [{ value: 'publicarYa', label: t('projects.maintenance.articles.publishNow') }] : []),
      ]
      : []),
    { value: 'aprobarSinFecha', label: t('projects.maintenance.articles.approveOnly') },
  ];
  function desdeAgenda(v: string): void {
    switch (v) {
      case 'agendarYEnviar': void agendarYEnviar(); break;
      case 'agendar': correr({ tipo: 'aprobar', fecha }, () => { setModo(null); }); break;
      case 'soloAgendar':
        correr({ tipo: 'fecha', fecha }, () => {
          setModo(null);
          toast.success(t('projects.maintenance.articles.onlyScheduled', { fecha: fmtDia(fecha) }));
        });
        break;
      case 'publicarYa': void publicarYa(); break;
      case 'aprobarSinFecha': correr({ tipo: 'aprobar' }, () => { setModo(null); }); break;
    }
  }

  const botones = (
    <>
      {propuesta
        ? (
          <>
            <Button size="sm" tone="cyan" fullWidth={!apilada} loading={accion.isPending} onClick={() => { correr({ tipo: 'aprobar' }); }}>
              {t('projects.maintenance.articles.approveProposed', { fecha: fmtDia(propuesta) })}
            </Button>
            <Button size="sm" variant="outline" fullWidth={!apilada} onClick={() => { abrirAgenda(); }}>
              {t('projects.maintenance.articles.changeDate')}
            </Button>
          </>
        )
        : (
          <Button size="sm" tone="cyan" fullWidth={!apilada} onClick={() => { abrirAgenda(); }}>
            {t('projects.maintenance.articles.approveSchedule')}
          </Button>
        )}
      <Button size="sm" variant="outline" fullWidth={!apilada} onClick={() => { setModo(m => (m === 'cambios' ? null : 'cambios')); }}>
        {t('projects.maintenance.articles.changes')}
      </Button>
      <Dropdown size="sm" align="right" options={mas} value={null} onChange={desdeMas} placeholder={t('projects.maintenance.articles.more')} />
    </>
  );

  return (
    <div ref={tarjeta} style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', overflow: 'hidden' }}>
      <div style={{
        ...REJILLA,
        gridTemplateColumns: apilada ? 'var(--evo-articulo-portada-w) minmax(0, 1fr)' : REJILLA.gridTemplateColumns,
        padding: 'var(--evo-space-4)',
      }}>
        {/* La portada, o el lugar para soltarla. Sin portada, el hueco ES la
            zona de subida (Eduardo, 2026-09-06: "poder subir la portada
            arrastrando una imagen al placeholder"). Con portada, «Más» abre
            la misma zona debajo de la imagen. */}
        <div style={{ minWidth: 0 }}>
          {portada && <img src={portada} alt="" style={{ display: 'block', width: '100%', height: 'var(--evo-articulo-portada-h)', objectFit: 'cover', borderRadius: 'var(--evo-r)' }} />}
          {(!portada || subiendoPortada) && (
            <div style={{ marginTop: portada ? 'var(--evo-space-2)' : 0 }}>
              <FileUploadZone
                uploadUrl={`/api/projects/${String(projectId)}/articulos/${String(articulo.id)}/portada/subir`}
                value={[]}
                acceptMultiple={false}
                acceptedTypes="image/png,image/jpeg,image/webp"
                dragLabel={t('projects.maintenance.articles.coverDrop')}
                onUploaded={() => {
                  setSubiendoPortada(false);
                  toast.success(t('projects.maintenance.articles.coverUploaded'));
                  void qc.invalidateQueries({ queryKey: ['articulos', projectId] });
                }}
                onDelete={() => { /* no hay lista de archivos que borrar */ }}
              />
              {portada && (
                <Button size="xs" variant="link" tone="muted" onClick={() => { setSubiendoPortada(false); }}>
                  {t('projects.maintenance.articles.coverCancel')}
                </Button>
              )}
            </div>
          )}
        </div>

        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>{articulo.title}</span>
            {articulo.traducciones.length > 0 && (
              <Pill tone="muted" variant="outline" size="xs">{[articulo.lang, ...articulo.traducciones.map(v => v.lang)].map(l => l.toUpperCase()).join(' · ')}</Pill>
            )}
            {articulo.status === 'cambios' && (
              <Pill tone="amber" variant="subtle" size="xs">{t('projects.maintenance.articles.changes')}</Pill>
            )}
            {articulo.clientDecision === 'aprobado' && (
              <Pill tone="cyan" variant="outline" size="xs">{t('projects.maintenance.articles.clientApproved')}</Pill>
            )}
            {articulo.clientDecision === 'comentado' && (
              <Pill tone="amber" variant="outline" size="xs">{t('projects.maintenance.articles.clientCommented')}</Pill>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-3)' }}>
            {/* Un enlace por idioma cuando el sitio tiene varias versiones: la
                pieza se ve en /blog/ y en /en/blog/, cada una con su portada. */}
            {[{ id: articulo.id, lang: articulo.lang }, ...articulo.traducciones].map(v => (
              <Button key={v.id} size="xs" variant="link" tone="cyan" onClick={() => { verComoQueda(projectId, v.id); }}>
                {t('projects.maintenance.articles.preview')}{articulo.traducciones.length > 0 ? ` ${v.lang.toUpperCase()}` : ''} ↗
              </Button>
            ))}
          </div>

          {modo === 'agendar' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)', alignItems: 'center', marginTop: 'var(--evo-space-2)' }}>
              <Input value={fecha} onChange={setFecha} type="date" size="sm" />
              <Dropdown
                size="sm"
                options={opcionesDeAgenda}
                value={null}
                onChange={desdeAgenda}
                disabled={accion.isPending}
                placeholder={t('projects.maintenance.articles.chooseAction')}
              />
              {/* La fecha de hoy ya pasó "la hora": sale en el próximo barrido
                  de diez minutos, o ya mismo si él lo decide desde el menú. Se
                  dice acá, donde eligió la fecha, para que no tenga que adivinarlo. */}
              {fecha === hoyLocal() && (
                <span style={{ ...menor, flexBasis: '100%' }}>{t('projects.maintenance.articles.todayHint')}</span>
              )}
            </div>
          )}
          {modo === 'cambios' && (
            <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'flex-start', marginTop: 'var(--evo-space-2)' }}>
              <Textarea value={nota} onChange={setNota} rows={2} fullWidth placeholder={t('projects.maintenance.articles.changesNote')} />
              <Button size="xs" variant="outline" disabled={!nota.trim()} loading={accion.isPending} onClick={() => { correr({ tipo: 'cambios', nota: nota.trim() }, () => { setNota(''); setModo(null); }); }}>
                {t('projects.maintenance.articles.changes')}
              </Button>
            </div>
          )}
        </div>

        {/* Los botones, cada uno en una línea, del mismo tamaño; «Más» es un
            botón como los otros. La columna es ancha para que ninguna
            etiqueta se parta (Eduardo, 2026-09-06). Angosta: en fila, debajo. */}
        {!apilada && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', whiteSpace: 'nowrap' }}>
            {botones}
          </div>
        )}
      </div>
      {apilada && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-2)', padding: '0 var(--evo-space-4) var(--evo-space-4)', whiteSpace: 'nowrap' }}>
          {botones}
        </div>
      )}

      {/* El comentario del cliente es el motivo de que el artículo esté
          esperando: va al pie, con su rótulo. */}
      {articulo.clientComment && (
        <div style={{ borderTop: '1px solid var(--evo-stroke)', background: 'var(--evo-panel)', padding: 'var(--evo-space-2) var(--evo-space-3)' }}>
          <div style={{ ...etiqueta, marginBottom: '2px' }}>{t('projects.maintenance.articles.clientCommentLabel')}</div>
          <div style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{articulo.clientComment}</div>
        </div>
      )}

      {dialog}
    </div>
  );
}

/** Una fecha YYYY-MM-DD a texto sin que el huso la corra un día (medianoche UTC en Florida es el día anterior). */
function fechaDelDia(f: string, lang: string): string {
  return new Date(`${f.slice(0, 10)}T12:00:00Z`).toLocaleDateString(lang, { day: 'numeric', month: 'short' });
}

/**
 * La fecha de un agendado, editable: se toca, se elige otra, se guarda. Aprobar
 * de nuevo con fecha es lo que la cambia en el servidor (Eduardo, 2026-09-06:
 * "después que ya aprobé y agendé, como quiera debería poder cambiar la fecha").
 */
function FechaAgendada({ articulo, projectId }: { articulo: Articulo; projectId: number }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const accion = useAccionArticulo(projectId);
  const [editando, setEditando] = useState(false);
  const [fecha, setFecha] = useState(articulo.scheduledFor?.slice(0, 10) ?? '');
  if (!editando) {
    return (
      <Button
        size="xs"
        variant="link"
        tone={articulo.scheduledFor ? 'cyan' : 'muted'}
        onClick={() => { setFecha(articulo.scheduledFor?.slice(0, 10) ?? ''); setEditando(true); }}
      >
        {articulo.scheduledFor ? fechaDelDia(articulo.scheduledFor, i18n.language) : t('projects.maintenance.articles.noDate')}
      </Button>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-1)' }}>
      <Input value={fecha} onChange={setFecha} type="date" size="sm" />
      <Button
        size="xs"
        tone="cyan"
        disabled={!fecha}
        loading={accion.isPending}
        onClick={() => {
          // Cambiar la fecha de uno YA aprobado se hace aprobando de nuevo; de
          // uno que todavía espera al cliente, NO — eso lo aprobaría a sus
          // espaldas.
          const accionDeFecha = articulo.status === 'aprobado'
            ? { tipo: 'aprobar' as const, fecha }
            : { tipo: 'fecha' as const, fecha };
          accion.mutate({ articuloId: articulo.id, accion: accionDeFecha }, {
            onSuccess: () => { setEditando(false); toast.success(t('projects.maintenance.articles.dateSaved')); },
            onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
          });
        }}
      >
        {t('projects.maintenance.articles.save')}
      </Button>
      <Button size="xs" variant="link" tone="muted" onClick={() => { setEditando(false); }}>{t('projects.maintenance.articles.cancel')}</Button>
    </span>
  );
}

/** Borrar un agendado. Pregunta: es de lo que no vuelve. */
function BorrarAgendado({ articulo, projectId }: { articulo: Articulo; projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const accion = useAccionArticulo(projectId);
  const { confirm, dialog } = useConfirm();
  async function borrar(): Promise<void> {
    const ok = await confirm({
      title: t('projects.maintenance.articles.deleteTitle'),
      description: t('projects.maintenance.articles.deleteBody'),
      confirmLabel: t('projects.maintenance.articles.delete'),
      tone: 'danger',
    });
    if (!ok) return;
    accion.mutate({ articuloId: articulo.id, accion: { tipo: 'borrar' } }, {
      onSuccess: () => { toast.success(t('projects.maintenance.articles.deleted')); },
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }
  return (
    <>
      <Button size="xs" variant="link" tone="muted" loading={accion.isPending} onClick={() => { void borrar(); }}>
        {t('projects.maintenance.articles.delete')}
      </Button>
      {dialog}
    </>
  );
}

/**
 * Publicar un agendado ya, sin esperar el barrido. Pregunta: escribe en el
 * sitio del cliente. Es el caso "lo puse para hoy y lo quiero afuera ahora"
 * (Scrapmen, 2026-09-07).
 */
function PublicarAgendado({ articulo, projectId }: { articulo: Articulo; projectId: number }) {
  const { t } = useTranslation();
  const toast = useToast();
  const accion = useAccionArticulo(projectId);
  const { confirm, dialog } = useConfirm();
  async function publicar(): Promise<void> {
    const ok = await confirm({
      title: t('projects.maintenance.articles.publishNowTitle'),
      description: t('projects.maintenance.articles.publishNowBody'),
      confirmLabel: t('projects.maintenance.articles.publishNow'),
      tone: 'warning',
    });
    if (!ok) return;
    accion.mutate({ articuloId: articulo.id, accion: { tipo: 'publicar' } }, {
      onSuccess: () => { toast.success(t('projects.maintenance.articles.publishedNow')); },
      onError: e => { toast.error(e instanceof Error ? e.message : t('common.error')); },
    });
  }
  return (
    <>
      <Button size="xs" variant="link" tone="cyan" loading={accion.isPending} onClick={() => { void publicar(); }}>
        {t('projects.maintenance.articles.publishNow')}
      </Button>
      {dialog}
    </>
  );
}

/**
 * Qué le pasa a un agendado cuya fecha ya llegó. En semi y auto, el barrido de
 * diez minutos lo saca solo; en manual nada sale solo y hay que decirlo, o el
 * artículo "de hoy" se queda ahí y nadie entiende por qué.
 */
function notaDeVencido(a: Articulo, autonomia: string, t: (k: string) => string): string | null {
  const f = a.scheduledFor?.slice(0, 10);
  if (!f || f > hoyLocal()) return null;
  return autonomia === 'manual' ? t('projects.maintenance.articles.dueTodayManual') : t('projects.maintenance.articles.dueToday');
}

/** Lo agendado y lo publicado: historial, no trabajo. Dos columnas, filas cortas, por fecha. */
function LibroMayor({ projectId, agendados, publicados, base, autonomia }: { projectId: number; agendados: Articulo[]; publicados: Articulo[]; base: string | null; autonomia: string }) {
  const { t, i18n } = useTranslation();
  const fmt = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  // Por fecha, como la tira de arriba; los sin fecha al final.
  const porFecha = [...agendados].sort((a, b) => (a.scheduledFor ?? '9999').localeCompare(b.scheduledFor ?? '9999'));
  const cabecera = (texto: string) => (
    <div style={{ ...etiqueta, padding: 'var(--evo-space-2) var(--evo-space-3)', background: 'var(--evo-bg-2)', borderBottom: '1px solid var(--evo-stroke)' }}>{texto}</div>
  );
  const fila = (a: Articulo, derecha: React.ReactNode, ultima: boolean, nota?: string | null) => (
    <div key={a.id} style={{ padding: 'var(--evo-space-2) var(--evo-space-3)', borderBottom: ultima ? 'none' : '1px solid var(--evo-stroke)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>
          {a.title}
          {a.traducciones.length > 0 && <span style={{ ...menor, marginLeft: 'var(--evo-space-2)' }}>{[a.lang, ...a.traducciones.map(v => v.lang)].map(l => l.toUpperCase()).join(' · ')}</span>}
        </span>
        {derecha}
      </div>
      {nota && <div style={{ ...menor, marginTop: '2px' }}>{nota}</div>}
    </div>
  );
  return (
    <div style={{ border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', overflow: 'hidden' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)' }}>
        <div style={{ borderRight: '1px solid var(--evo-stroke)' }}>
          {cabecera(t('projects.maintenance.articles.scheduledN', { count: agendados.length }))}
          {porFecha.map((a, i) => fila(
            a,
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
              <FechaAgendada articulo={a} projectId={projectId} />
              <PublicarAgendado articulo={a} projectId={projectId} />
              <BorrarAgendado articulo={a} projectId={projectId} />
            </span>,
            i === porFecha.length - 1,
            notaDeVencido(a, autonomia, t) ?? (a.clientDecision === 'aprobado' ? t('projects.maintenance.articles.clientApproved') : null),
          ))}
        </div>
        <div>
          {cabecera(t('projects.maintenance.articles.publishedN', { count: publicados.length }))}
          {publicados.map((a, i) => fila(
            a,
            base
              ? (
                <a href={`${base}${a.slug}/`} target="_blank" rel="noopener noreferrer" style={{ ...menor, color: 'var(--evo-cyan)', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                  {a.publishedAt ? fmt(a.publishedAt) : ''} ↗
                </a>
              )
              : <span style={{ ...menor, whiteSpace: 'nowrap' }}>{a.publishedAt ? fmt(a.publishedAt) : ''}</span>,
            i === publicados.length - 1,
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Siete días desde la última revisión, con los artículos que caen en cada
 * uno: los que esperan aprobación con su fecha propuesta, y los agendados.
 * Sin revisión todavía, arranca en el lunes de esta semana.
 */
function TiraDeLaSemana({ articulos, desde }: { articulos: Articulo[]; desde: string | null }) {
  const { t, i18n } = useTranslation();
  const inicio = desde ? new Date(desde) : new Date();
  // Al mediodía UTC para que sumar días no cruce un cambio de hora.
  const base = new Date(Date.UTC(inicio.getUTCFullYear(), inicio.getUTCMonth(), inicio.getUTCDate(), 12));
  if (!desde) {
    // Sin revisión: el lunes de esta semana.
    const dia = base.getUTCDay();
    base.setUTCDate(base.getUTCDate() - ((dia + 6) % 7));
  }
  const dias = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(base.getTime() + i * 86_400_000);
    return d.toISOString().slice(0, 10);
  });
  const porDia = new Map<string, Articulo[]>();
  for (const a of articulos) {
    const f = a.scheduledFor?.slice(0, 10);
    if (!f) continue;
    porDia.set(f, [...(porDia.get(f) ?? []), a]);
  }
  const sinFecha = articulos.filter(a => !a.scheduledFor);
  const hoy = hoyLocal();
  return (
    <div style={{ borderBottom: '1px solid var(--evo-stroke)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
        {dias.map((f, i) => {
          const lista = porDia.get(f) ?? [];
          const d = new Date(`${f}T12:00:00Z`);
          return (
            <div key={f} style={{ padding: 'var(--evo-space-2) var(--evo-space-2)', borderRight: i === 6 ? 'none' : '1px solid var(--evo-stroke)', minWidth: 0, background: f === hoy ? 'var(--evo-panel)' : undefined }}>
              <div style={{ ...etiqueta, color: f === hoy ? 'var(--evo-cyan)' : 'var(--evo-fg-4)' }}>
                {d.toLocaleDateString(i18n.language, { weekday: 'short' })} {String(d.getUTCDate())}
              </div>
              {lista.map(a => (
                <div key={a.id} title={a.title} style={{ ...menor, marginTop: '2px', color: a.status === 'aprobado' ? 'var(--evo-cyan)' : 'var(--evo-fg-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.status === 'aprobado' ? '● ' : '○ '}{a.title}
                </div>
              ))}
            </div>
          );
        })}
      </div>
      <div style={{ ...menor, padding: 'var(--evo-space-1) var(--evo-space-4) var(--evo-space-2)' }}>
        {t('projects.maintenance.articles.weekLegend')}
        {sinFecha.length > 0 ? ` · ${t('projects.maintenance.articles.weekNoDate', { count: sinFecha.length })}` : ''}
      </div>
    </div>
  );
}
